<script setup lang="ts">
  import { ref, onMounted, provide, watch, onUnmounted, computed } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import AppHeaderEdu from '@/components/AppHeaderEdu/AppHeaderEdu.vue'
  import shortLinkRewrite from '@/utils/shortLinkRewrite'
  import emitter, { EventBusKey } from '@/utils/eventBusMitt'
  import commoneEmitter, { EventCommonBusKey } from '@zaohaowu/website-common/utils/eventBusMitt'
  import getDomains from '@/utils/getDomains'
  import EduWelcomModal from '@/components/EduWelcomModal/index.vue'
  import EduHistoryModal from '@/components/EduHistoryModal/index.vue'
  import { useUserStore } from '@zaohaowu/website-common/stores/userStore'
  import messageQueue from '@/utils/messageQueue'
  import httpApi from '@/http/api'
  import { useConfigStore } from '@/stores/configStore'
  import { LocalStorageKey } from '@/utils/constants'
  import AppHeader from '@zaohaowu/website-common/components/AppHeader/index.vue'
  import getUserInfo from '@/utils/getUserInfo'
  import http from '@/http'
  import { useTicketStore } from '@zaohaowu/website-common/stores/userTicket'
  import { SHOP_BASE_URL } from '@/utils/constants'
  import isMobile from '@/utils/isMobile'
  import H5 from './components/H5/index.vue'
  import { useFetchDialogData } from '@/hooks/useFetchDialogData'
  import ActivityDialog from './components/ActivityDialog/index.vue'
  import { useDialogStore } from '@/stores/dialog'
  interface HomeCreateNew {
    curKey: string
    preKey?: string
    newVisible: boolean
  }

  defineOptions({ name: 'App' })

  const isH5 = isMobile()
  const ticketStore = useTicketStore()
  const newConfigStore = useConfigStore()
  const userStore = useUserStore()
  const dialogStore = useDialogStore()
  const catchComponentNames = [
    'AppFeeds',
    'AppHome',
    'AppChallengeDetail',
    'AppAllActivities',
    'AppAccount',
    'customerAccount',
    'AppOrderList',
    'home'
  ].join(',')
  const cachedComponents = ref(catchComponentNames.split(','))
  const route = useRoute()
  const router = useRouter()
  const headerTransparent = ref(false)
  const showCreateEntry = ref(true)
  const needFooter = ref(true)
  const footerDelay = ref(2000)
  const generatorCacheNames = ref<string[]>([])
  const isShowEduWelcomModal = ref(false)
  const isShowEduHistoryModal = ref(false)
  const mainSiteContainerRef = ref<HTMLElement>()

  // 是否为教育站点
  const isEdu = getDomains().thirdLevelDomain === 'edu'

  // 说明：在打包后 route.meta 可能为空，是因为 vue-router 的 route 对象在某些情况下不会自动带上 meta，需要从 matched 最后一项获取
  const needHeader = computed(() => {
    // 优先从 route.meta 取，如果没有则从 matched 最后一项的 meta 取
    let meta = route.meta

    if (!meta || Object.keys(meta).length === 0) {
      const matched = route.matched
      if (matched && matched.length > 0) {
        meta = matched[matched.length - 1].meta || {}
      }
    }
    return meta.needHeader === undefined || meta.needHeader === true
  })

  // 清除缓存
  const clearKeepCache = (keys: string[] = []) => {
    if (keys.length > 0) {
      keys.forEach(key => {
        const index = cachedComponents.value.indexOf(key)
        if (index > -1) {
          cachedComponents.value.splice(index, 1)
        }
      })
    } else {
      cachedComponents.value = []
    }
    window.setTimeout(() => {
      cachedComponents.value = catchComponentNames.split(',')
    }, 200)
  }

  provide('clearKeepCache', clearKeepCache)

  // 工具页点击外部隐藏历史记录抽屉
  const hideHistorySlide = (e: MouseEvent) => {
    const historySlideContainer = document.getElementById('history-slide-container')
    const target = e.target as HTMLElement
    if (historySlideContainer && !historySlideContainer.contains(target)) {
      emitter.emit(EventBusKey.showHistorySlide, false)
    }
  }

  // 获取New标识配置
  const getNewTagConfig = async () => {
    const res: any = await httpApi.getNewTagConfig()
    newConfigStore.setNewTagConfig(res.data)
    const homeCreatNew = localStorage.getItem(LocalStorageKey.homeCreatNew)
    // 如果本地没有存，则存入接口返回的
    if (!homeCreatNew) {
      localStorage.setItem(LocalStorageKey.homeCreatNew, JSON.stringify(res.data.homeCreatNew))
    } else {
      // 如果本地有存，则比较curKey，如果curKey不一致，则存入接口返回的
      const homeCreatNewObj: HomeCreateNew = JSON.parse(homeCreatNew)
      if (
        homeCreatNewObj.curKey !== res.data.homeCreatNew.curKey ||
        !res.data.homeCreatNew.newVisible
      ) {
        localStorage.setItem(LocalStorageKey.homeCreatNew, JSON.stringify(res.data.homeCreatNew))
      }
    }
  }

  const getUser = async () => {
    try {
      const res: any = await getUserInfo()
      userStore.setIsLogin(true)
      userStore.setUser(res.data)
      ticketStore.setTicket(res.data.balance || '0')
      if (!isEdu) {
        renewalToken()
      }
    } catch (error) {
      userStore.setIsLogin(false)
      userStore.clearUser()
    }
  }

  // 续约token
  const renewalToken = () => {
    http.get('/aigc/api/auth/renewalToken')
  }

  const generatorRoutes = [
    'generatorImage',
    'generatorInpainting',
    'generator3D',
    'generatorHD',
    'generatorImg2Img',
    'generatorVideo'
  ]

  onMounted(async () => {
    mainSiteContainerRef.value?.click()
    await getUser()

    if (!isEdu) {
      getNewTagConfig()
    }

    shortLinkRewrite()
    if (!isEdu) {
      useFetchDialogData(router)
    }
    router.afterEach((to, from) => {
      // 这部分逻辑只针对工具页缓存
      // 如果to的页面是需要缓存的，把组件name加入缓存数组
      if (to.meta.keepAlive) {
        !generatorCacheNames.value.includes(to.name as string) &&
          generatorCacheNames.value.push(to.name as string)
      }
      // 如果from的页面是需要缓存的，并且to的页面深度小于from的页面深度，则把from的页面从缓存数组中移除
      if (from.meta.keepAlive && (to.meta.depth as number) < (from.meta.depth as number)) {
        if (generatorCacheNames.value.includes(from.name as string)) {
          generatorCacheNames.value = generatorCacheNames.value.filter(name => name !== from.name)
        }
      }

      // 教育版工具页需要增加返回按钮
      if (to.name === 'generator') {
        emitter.emit(EventBusKey.eduHeaderBackBtnVisible, true)
        messageQueue.send(EventBusKey.eduHeaderBackBtnVisible, { visible: true })
      } else {
        emitter.emit(EventBusKey.eduHeaderBackBtnVisible, false)
        messageQueue.send(EventBusKey.eduHeaderBackBtnVisible, { visible: false })
      }

      // 工具页最小高度400px,其他页面最小高度750px
      if (mainSiteContainerRef.value) {
        if (generatorRoutes.includes(to.name as string)) {
          mainSiteContainerRef.value!.style.minHeight = '400px'
          mainSiteContainerRef.value!.style.minWidth = '1200px'
        } else {
          mainSiteContainerRef.value!.style.minHeight = '750px'
          mainSiteContainerRef.value!.style.minWidth = '1024px'
        }
      }

      if (!to.meta.scrollTopClose) {
        window.scrollTo(0, 0)
      }

      // 用于埋点参数的PageName
      localStorage.setItem('currentPageName', to.name as string)
      localStorage.setItem('previousPageName', from.name as string)
      localStorage.setItem('previousPagePath', from.path as string)
    })

    window.addEventListener('click', hideHistorySlide)

    emitter.on(EventBusKey.showEduHistoryModal, () => {
      isShowEduHistoryModal.value = true
    })
    commoneEmitter.on(EventCommonBusKey.showEduWelcomModal, () => {
      isShowEduWelcomModal.value = true
    })

    // Rem适配
    setRem()
    window.addEventListener('resize', setRem)

    onUnmounted(() => {
      window.removeEventListener('click', hideHistorySlide)
      window.removeEventListener('resize', setRem)
      window.removeEventListener('load', setRem)
      emitter.off(EventBusKey.showEduHistoryModal)
      commoneEmitter.off(EventCommonBusKey.showEduWelcomModal)
    })
  })

  watch(
    () => route.path,
    () => {
      headerTransparent.value = (route.meta.headerTransparent as boolean | undefined) ?? false
      showCreateEntry.value = (route.meta.showCreateEntry as boolean | undefined) ?? true
      needFooter.value = (route.meta.needFooter as boolean | undefined) ?? true
      footerDelay.value = (route.meta.footerDelay as number | undefined) ?? 2000
    },
    { immediate: true }
  )

  const getDeviceWidth = () => {
    return Math.min(
      document.documentElement.clientWidth || document.body.clientWidth,
      window.innerWidth
    )
  }

  // Rem适配
  const setRem = () => {
    const designWidth = 1440 //设计稿宽度
    const remBase = 10 // 基准值, 1rem = 10px
    let deviceWidth = getDeviceWidth()
    // 限制最大和最小宽度，防止过大或过小, 320为最小宽度，1920为最大宽度
    deviceWidth = Math.min(Math.max(deviceWidth, 1024), 1920)
    const fontSize = (deviceWidth / designWidth) * remBase
    document.documentElement.style.fontSize = `${fontSize}px`
  }
</script>

<template>
  <H5 v-if="isH5" />
  <!-- 主站新版 -->
  <div class="main-site-container" v-else-if="!isEdu" ref="mainSiteContainerRef">
    <AppHeader v-if="needHeader" type="community" :base="SHOP_BASE_URL" />
    <div class="router-view-container">
      <router-view v-slot="{ Component }">
        <keep-alive :include="[...cachedComponents, ...generatorCacheNames]">
          <component :key="$route.fullPath" :is="Component" />
        </keep-alive>
      </router-view>
    </div>
    <ActivityDialog
      :visible="dialogStore.isIncentiveDialogVisible"
      :title="dialogStore.incentiveDialogData?.content || ''"
      :headerImage="dialogStore.incentiveDialogData?.activeImage"
      :buttonText="dialogStore.incentiveDialogData?.buttonText || ''"
      :type="dialogStore.incentiveDialogData?.type"
      :imageList="dialogStore.incentiveDialogData?.productImage"
      @close="dialogStore.closeIncentiveDialog"
    />
  </div>

  <!-- Edu版本保持不变 -->
  <template v-else>
    <AppHeaderEdu v-if="needHeader" :headerTransparent="headerTransparent" />
    <router-view v-slot="{ Component }">
      <keep-alive :include="[...cachedComponents, ...generatorCacheNames]">
        <component :key="$route.fullPath" :is="Component" />
      </keep-alive>
    </router-view>
  </template>

  <!-- 主站承接学生，欢迎弹窗 -->
  <EduWelcomModal
    v-if="!isEdu && isShowEduWelcomModal"
    :onClose="
      () => {
        isShowEduWelcomModal = false
      }
    "
  />

  <!-- 教育站承接学生，历史记录弹窗 -->
  <EduHistoryModal
    v-if="!isEdu && isShowEduHistoryModal"
    :onClose="
      () => {
        isShowEduHistoryModal = false
      }
    "
  />
</template>

<style lang="stylus" scoped>
  @import './index.styl';
</style>
